import { APIProvider, useMapsLibrary, Map } from "@vis.gl/react-google-maps";
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useCallbackRef,
  useDebouncedEffect,
  useDeepCompareEffect,
} from "./utility-hooks";

import { Coordinate } from "@/types/coordinate";
import { useExplorationStore } from "@/store/explorationStore";
import { useNavigate } from "react-router-dom";
import i18n from "@/i18n/i18n";
import { t } from "i18next";
import {
  findVenueCenter,
  getSupportedVenueData,
  getVenueModels,
} from "@/types/supported-venue";
import Text from "@/components/text/text";

// import {
//   isVenueSupported,
// } from "@/const/venue_model/venue_model_config";
import {
  isVenueSupported,
  getVenueModelConfigByVenueCode,
  getBaseUrl,
  getVenueIdByVenueCode,
} from "src/utils/map-util";

import { findWorldMapModel } from "@/api/beetle";
import { WorldMapData, WorldMapListJson } from "@/types/worldmap";

const BASE_URL = getBaseUrl();

export type Map3DProps = google.maps.maps3d.Map3DElementOptions & {
  onCameraChange?: (cameraProps: Map3DCameraProps) => void;
  onZoomIn?: () => void;
  showToast?: (venueName: string) => void;
  hideToast?: () => void;
  selectedVenue?: string;
  isFlutter: boolean;
};

export type Map3DCameraProps = {
  center: google.maps.LatLngAltitudeLiteral;
  range: number;
  heading: number;
  tilt: number;
  roll: number;
};

type LatLng = { lat: number; lng: number };

type MapBounds = {
  northeast: string;
  southwest: string;
  zoomLevel: string;
};

// let globalUserId = "";

// declare global {
//   interface Window {
//     FlutterAPI: {
//       setUserId: (arg: string) => void;
//     };
//   }
// }

// window.FlutterAPI = {
//   setUserId: (userId: string) => {
//     console.log("FlutterAPI.setUserId:", userId);
//     globalUserId = userId;
//   }
// };

const PolygonWithLabel = ({
  code,
  name,
  coords,
  center,
  onClick,
}: {
  code: string;
  name: string;
  coords: number[][];
  center: Coordinate;
  onClick: Function;
}) => {
  const polygonRef = useRef<any>(null);

  const convertCoords = (geoJsonCoords: number[][]) => {
    return geoJsonCoords.map((coord) => ({
      lng: coord[0],
      lat: coord[1],
      altitude: coord[2] || 100,
    }));
  };

  useEffect(() => {
    const polygon = polygonRef.current;
    if (polygon) {
      polygon.outerCoordinates = convertCoords(coords);
      // Handle Click Action
      const handleClick = () => {
        onClick(code, name, center);
      };

      polygon.addEventListener("gmp-click", handleClick);
      return () => {
        polygon.removeEventListener("gmp-click", handleClick);
      };
    }
  }, [coords, name]);

  const labelPos = {
    lat: center.lat,
    lng: center.lng,
    altitude: 0,
  };

  return (
    <>
      <gmp-polygon-3d-interactive
        ref={polygonRef}
        fill-color="rgba(0, 252, 42, 0.4)"
        stroke-color="rgba(0, 252, 42, 0.8)"
        stroke-width="3"
        extruded
        altitude-mode="relative-to-ground"
      />
      <gmp-marker-3d
        position={labelPos}
        label={name}
        collision-behavior="OPTIONAL_AND_HIDES_LOWER_PRIORITY"
      >
        <template>
          <div style={{ display: "none" }}></div>
        </template>
      </gmp-marker-3d>
    </>
  );
};

const GoogleMap3D = forwardRef(
  (
    props: Map3DProps,
    forwardedRef: ForwardedRef<google.maps.maps3d.Map3DElement | null>,
  ) => {
    useMapsLibrary("maps3d");
    const geometryLib = useMapsLibrary("geometry");
    const setCurrentSplat = useExplorationStore(
      (state) => state.setCurrentSplat,
    );
    const setCurrentVenue = useExplorationStore(
      (state) => state.setCurrentVenue,
    );
    const setCurrentLevel = useExplorationStore(
      (state) => state.setCurrentLevel,
    );
    let navigate = useNavigate();

    const internalRef = useRef<google.maps.maps3d.Map3DElement | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [mapElement, setMapElement] =
      useState<google.maps.maps3d.Map3DElement | null>(null);
    const [smallModelData, setSmallModelData] =
      useState<WorldMapListJson | null>(null);
    const rawVenueData = getSupportedVenueData();
    const venueData = useMemo(() => {
      return rawVenueData.map((venue) => {
        const translatedFeatures = venue.features.map((feature: any) => {
          const translationKey = feature.properties.name;
          const translatedName = t(translationKey);

          return {
            ...feature,
            properties: {
              ...feature.properties,
              name: translatedName,
            },
          };
        });

        return {
          ...venue,
          features: translatedFeatures,
        };
      });
    }, [rawVenueData, t, i18n.language]);

    const [map3DElement, map3dRef] =
      useCallbackRef<google.maps.maps3d.Map3DElement>();

    const [customElementsReady, setCustomElementsReady] = useState(false);
    // const [geometryLibraryReady, setGeometryLibraryReady] = useState(false);
    // const [mapBoundary, setMapBoundary] = useState<MapBounds | null>(null);
    // const mapBoundaryRef = useRef<typeof mapBoundary>(mapBoundary);

    // useEffect(() => {
    //   if (!geometryLib || !internalRef.current) return;
    //   setGeometryLibraryReady(true)
    // }, [geometryLib, internalRef.current]);

    function boundsEqual(a?: MapBounds | null, b?: MapBounds | null) {
      if (!a || !b) return false;
      return a.northeast === b.northeast && a.southwest === b.southwest;
    }

    // useEffect(() => {
    //   mapBoundaryRef.current = mapBoundary;
    // }, [mapBoundary]);

    // useEffect(() => {
    //   if (geometryLibraryReady && props.isFlutter) {
    //     const mapElement = internalRef.current;
    //     mapElement?.addEventListener('gmp-steadychange', async () => {
    //       const bounds = getBoundsAndZoom();
    //       if (!boundsEqual(bounds, mapBoundaryRef.current)) {
    //         setMapBoundary(bounds);
    //         mapBoundaryRef.current = bounds;
    //       }
    //     });
    //   }
    // }, [geometryLibraryReady]);

    // useDebouncedEffect(
    //   () => {
    //     console.log("mapBoundary call api", mapBoundaryRef.current)
    //     console.log("props.beetleUser", globalUserId)
    //     if (mapBoundaryRef.current && globalUserId && props.isFlutter) {
    //       retrieveSmallModel(globalUserId, `${mapBoundaryRef.current?.northeast},${mapBoundaryRef.current?.southwest}`, mapBoundaryRef.current.zoomLevel)
    //     }
    //   },
    //   500,
    //   [mapBoundaryRef.current, globalUserId]
    // );

    useEffect(() => {
      customElements.whenDefined("gmp-map-3d").then(() => {
        setCustomElementsReady(true);
        setIsMapReady(true);
        if (props.selectedVenue && props.selectedVenue !== "map") {
          setTimeout(() => {
            zoomOut();
          }, 1500);
        }
      });
    }, []);

    // const retrieveSmallModel = async (userId: string, coordinates: string, zoomLevel: string) => {

    //   const result = await findWorldMapModel(userId, coordinates, zoomLevel)
    //   console.log("retrieveSmallModel result", result);
    //   setSmallModelData(result);
    //   // addSmallModelMarkers();
    // }

    // const getBoundsAndZoom = (): MapBounds | null => {

    //   if (!geometryLib || !internalRef.current) return null;

    //   const { spherical } = geometryLib;
    //   const map3D = internalRef.current;

    //   if (!map3D) return null;

    //   if (map3D.center && map3D.range) {
    //     const RANGE_AT_ZOOM_0 = 40075000;
    //     const zoomLevel = Math.log2(RANGE_AT_ZOOM_0 / map3D.range);

    //     const center = new google.maps.LatLng(map3D?.center.lat, map3D?.center.lng);
    //     const radius = map3D.range * 0.38;

    //     const ne = spherical.computeOffset(center, radius * 1.414, 45);
    //     const sw = spherical.computeOffset(center, radius * 1.414, 225);

    //     return {
    //       northeast: `${ne.lng()},${ne.lat()}`,
    //       southwest: `${sw.lng()},${sw.lat()}`,
    //       zoomLevel: zoomLevel.toString()
    //     };
    //   } else {
    //     return null
    //   }
    // };

    const handleRef = useCallback(
      (element: google.maps.maps3d.Map3DElement | null) => {
        internalRef.current = element;

        setMapElement(element);

        if (typeof forwardedRef === "function") {
          forwardedRef(element);
        } else if (forwardedRef) {
          forwardedRef.current = element;
        }
      },
      [forwardedRef],
    );

    const { center, heading, tilt, range, roll, ...map3dOptions } = props;

    useDeepCompareEffect(() => {
      if (!map3DElement) return;

      Object.assign(map3DElement, map3dOptions);
    }, [map3DElement, map3dOptions]);

    useImperativeHandle<
      google.maps.maps3d.Map3DElement | null,
      google.maps.maps3d.Map3DElement | null
    >(forwardedRef, () => map3DElement, [map3DElement]);

    const zoomOut = () => {
      const mapElement = internalRef.current;

      if (
        !mapElement ||
        typeof (mapElement as any).flyCameraTo !== "function"
      ) {
        console.warn("Map3D element not ready for flyTo");
        return;
      }

      (mapElement as any).flyCameraTo({
        endCamera: {
          center: {
            lat: center?.lat,
            lng: center?.lng,
            altitude: 2000, //5000 * 280,
          },
          tilt: 0,
        },
        durationMillis: 1000,
      });
    };

    const handleClick = useCallback(
      (code: string, name: string, coordinate: Coordinate) => {
        const mapElement = internalRef.current;

        if (!isVenueSupported(code)) {
          const currentCenter = mapElement?.center;
          // prevent rerender and reset of map properties, save the current map center value
          const restoreMapCenter = () => {
            if (mapElement) {
              requestAnimationFrame(() => {
                mapElement.center = currentCenter;
              });
            }
          };

          if (props.showToast) props.showToast(name);

          restoreMapCenter();

          setTimeout(() => {
            if (props.hideToast) props.hideToast();
            restoreMapCenter();
          }, 3000);
          return;
        }

        if (
          !mapElement ||
          typeof (mapElement as any).flyCameraTo !== "function"
        ) {
          console.warn("Map3D element not ready for flyTo");
          return;
        }
        if (props.onZoomIn) {
          props.onZoomIn();
        }

        (mapElement as any).flyCameraTo({
          endCamera: {
            center: {
              lat: coordinate.lat,
              lng: coordinate.lng,
              altitude: 100,
            },
            tilt: 0,
          },
          durationMillis: 2000,
        });

        const venueCode = code;
        setCurrentVenue(venueCode);
        const venueModelConfig = getVenueModelConfigByVenueCode(venueCode);
        if (venueModelConfig) {
          //setCurrentSplat(venueModelConfig.baseUrl + venueModelConfig.mainSplat);
          //BASE_URL
          //setCurrentSplat(BASE_URL + venueModelConfig.baseUrl + venueModelConfig.mainSplat);
          setCurrentSplat(
            BASE_URL + venueModelConfig.folderPath + venueModelConfig.mainSplat,
          );
          if (venueModelConfig.floor) {
            const floorlist = venueModelConfig.floor.map((f) => f.name);
            const defaultFloor = venueModelConfig.floor.find(
              (f) => f.lcMapId === venueModelConfig.defaultLcMapId,
            );
            setCurrentLevel(defaultFloor?.name ?? floorlist[0]);
          }
        }

        setTimeout(() => {
          if (props.isFlutter) {
            navigate(`/explorationFlutter?venue=${venueCode}`);
          } else {
            navigate(`/explore3d/${venueCode}`);
          }
        }, 2100);
      },
      [navigate, setCurrentSplat, setCurrentVenue, setCurrentLevel],
    );

    const add3DModel = useCallback(async () => {
      const mapEl = internalRef.current;
      if (!mapEl) return;

      const maps3d = await google.maps.importLibrary("maps3d");
      const { Model3DElement } = maps3d as unknown as {
        Model3DElement: new (opts: {
          src: string;
          position: google.maps.LatLngAltitudeLiteral;
          orientation?: { heading?: number; tilt?: number; roll?: number };
          scale?: number;
          altitudeMode?: string;
        }) => HTMLElement;
      };
      const modelData = getVenueModels();
      modelData.forEach((venue) => {
        if (venue.model) {
          const model = new Model3DElement({
            src: venue.model.source,
            position: venue.model.center,
            orientation: { heading: 0, tilt: 270, roll: 0 },
            scale: 1,
            altitudeMode: "CLAMP_TO_GROUND",
          });
          (mapEl as HTMLElement).append(model);
        }
      });
    }, []);

    useEffect(() => {
      if (customElementsReady && internalRef.current) {
        add3DModel();
      }
    }, [customElementsReady, add3DModel]);

    const addVenueMarkers = useCallback(async () => {
      const mapEl = internalRef.current;
      if (!mapEl) return;

      const parser = new DOMParser();
      const response = await fetch("/assets/icon_venue_marker.svg");
      const pinSvgString = await response.text();
      const pinSvg = parser.parseFromString(
        pinSvgString,
        "image/svg+xml",
      ).documentElement;

      const positions: LatLng[] = venueData.flatMap((eachPolygon) =>
        eachPolygon.features.map((f: any) => f.properties.center as LatLng),
      );
      for (const venuePosition of positions) {
        const markerEl = document.createElement("gmp-marker-3d");
        const markerPosition: google.maps.LatLngAltitudeLiteral = {
          lat: venuePosition.lat,
          lng: venuePosition.lng,
          altitude: 10,
        };
        (
          markerEl as HTMLElement & {
            position: google.maps.LatLngAltitudeLiteral;
          }
        ).position = markerPosition;

        const template = document.createElement("template");
        template.content.append(pinSvg.cloneNode(true));
        markerEl.append(template);
        mapEl.appendChild(markerEl);
      }
    }, [venueData]);

    const addSmallModelMarkers = useCallback(async () => {
      const mapEl = internalRef.current;
      if (!mapEl) return;

      const parser = new DOMParser();
      const response = await fetch("/assets/small_model_marker.svg");
      const pinSvgString = await response.text();
      const pinSvg = parser.parseFromString(
        pinSvgString,
        "image/svg+xml",
      ).documentElement;

      const myMarkerResponse = await fetch("/assets/my_small_model_marker.svg");
      const myPinSvgString = await myMarkerResponse.text();
      const myPinSvg = parser.parseFromString(
        myPinSvgString,
        "image/svg+xml",
      ).documentElement;

      if (!smallModelData) return;

      // const positions: LatLng[] = smallModelData.data.flatMap((smallModel: WorldMapData) =>
      //   smallModel.coordinate as LatLng
      // );

      for (const data of smallModelData.data) {
        const markerEl = document.createElement(
          "gmp-marker-3d-interactive",
        ) as HTMLElement & {
          position: google.maps.LatLngAltitudeLiteral;
          addEventListener: (
            type: string,
            listener: (ev: Event) => void,
            options?: boolean | AddEventListenerOptions,
          ) => void;
          collisionBehavior: google.maps.CollisionBehavior;
          drawsWhenOccluded: boolean;
        };

        const markerPosition: google.maps.LatLngAltitudeLiteral = {
          lat: (data.coordinate as LatLng).lat,
          lng: (data.coordinate as LatLng).lng,
          altitude: 10,
        };
        markerEl.position = markerPosition;
        markerEl.collisionBehavior = google.maps.CollisionBehavior.REQUIRED;
        markerEl.drawsWhenOccluded = true;

        const template = document.createElement("template");
        if (data.isMyModel) {
          template.content.append(myPinSvg.cloneNode(true));
        } else {
          template.content.append(pinSvg.cloneNode(true));
        }
        markerEl.append(template);

        markerEl.addEventListener("gmp-click", (event) => {
          console.log("Marker clicked", data.id, event);
        });

        mapEl.appendChild(markerEl);
      }
    }, [smallModelData]);

    // const addListeners = async () => {
    //   const maps3d = await google.maps.importLibrary("maps3d");

    //   const map3DElement = document.getElementById('map');
    //   console.log("add Listeners", map3DElement)
    //   map3DElement?.addEventListener('gmp-steadychange', () => {
    //     // This runs after every camera move/zoom ends
    //     console.log('3D Map is now idle.');
    //   });
    // }
    // useEffect(() => {
    //   if (customElementsReady && internalRef.current && props.isFlutter) {
    //     addVenueMarkers();
    //     addSmallModelMarkers();
    //     addListeners()
    //   }
    // }, [customElementsReady, addVenueMarkers, addSmallModelMarkers]);

    if (!customElementsReady) return null;

    return (
      <gmp-map-3d
        ref={handleRef}
        center={center}
        range={props.range}
        heading={props.heading}
        tilt={props.tilt}
        roll={props.roll}
        mode="SATELLITE"
        gesture-handling="none"
      >
        {isMapReady &&
          venueData.map((eachPolygon) => {
            const name = eachPolygon.features[0].properties.name;
            return eachPolygon.features.map((polygon: any, index: number) => {
              return (
                <PolygonWithLabel
                  onClick={(
                    code: string,
                    name: string,
                    coordinate: Coordinate,
                  ) => handleClick(code, name, coordinate)}
                  key={`polygon-${name}-${index}`}
                  code={polygon.properties.code}
                  name={polygon.properties.name}
                  coords={polygon.geometry.coordinates[0]}
                  center={polygon.properties.center}
                />
              );
            });
          })}
      </gmp-map-3d>
    );
  },
);

const GoogleMap = ({
  onZoomIn,
  isFlutter = false,
  isConstrained = false,
  venueCode = "",
}: {
  onZoomIn?: () => void;
  isFlutter?: boolean;
  isConstrained?: boolean;
  venueCode?: string;
}) => {
  const API_KEY = process.env.REACT_APP_MAP_KEY || "";

  const getInitialViewProps = () => {
    const initProps = {
      center: {
        lat: 22.30237838835916,
        lng: 114.1671534828049,
        altitude: 1300,
      },
      range: 2000,
      heading: 0,
      tilt: 0,
      roll: 0,
    };

    if (venueCode === "map") {
      return initProps;
    }

    const center = findVenueCenter(venueCode);
    if (center) {
      return {
        center: { lat: center["lat"], lng: center["lng"], altitude: 100 },
        range: 50,
        heading: 0,
        tilt: 0,
        roll: 0,
      };
    }

    return initProps;
  };

  // const INITIAL_VIEW_PROPS = {
  //   center: { lat: 22.30237838835916, lng: 114.1671534828049, altitude: 1300 },
  //   range: 2000,
  //   heading: 0,
  //   tilt: 0,
  //   roll: 0,
  // };
  const [showToast, setShowToast] = useState(false);
  const [venueName, setVenueName] = useState("");
  const [viewProps, setViewProps] = useState(getInitialViewProps());

  useEffect(() => {
    setViewProps(getInitialViewProps());
  }, [venueCode]);

  return (
    <div className={`w-full  ${isConstrained ? "h-full" : "h-screen"} `}>
      <APIProvider apiKey={API_KEY}>
        <GoogleMap3D
          {...viewProps}
          onZoomIn={onZoomIn}
          showToast={(venueName) => {
            setShowToast(true);
            setVenueName(venueName);
          }}
          hideToast={() => setShowToast(false)}
          selectedVenue={venueCode}
          isFlutter={isFlutter}
        />
      </APIProvider>

      {showToast && (
        <div className="fixed z-[30] bg-overlay-map p-10 text-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-fade-in rounded-md">
          <Text
            className="primary-text font-semibold mb-5"
            text={venueName}
            size={18}
          />
          <Text
            className="primary-text font-semibold"
            text="coming_soon"
            size={14}
          />
        </div>
      )}
    </div>
  );
};
export default GoogleMap;
